import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackPurchase } from "@/lib/fbPixel";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const cleared = useRef(false);
  const pixelFired = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!pixelFired.current) { trackPurchase(data.order); pixelFired.current = true; }
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="h-12 w-12 mx-auto animate-spin" />
          <h1 className="font-heading font-black text-3xl tracking-tighter mt-6">CONFIRMING PAYMENT…</h1>
          <p className="font-mono text-sm text-subtle mt-2">Please wait, verifying with Stripe.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-success" className="border-2 border-ink shadow-hard p-10">
          <CheckCircle2 className="h-16 w-16 mx-auto text-success" />
          <h1 className="font-heading font-black text-4xl tracking-tighter mt-6">ORDER CONFIRMED</h1>
          <p className="font-mono text-sm text-subtle mt-3">Thanks for your purchase. A receipt has been emailed to you. We'll follow up with pickup or tracking details.</p>
          <Link to="/" className="inline-block mt-8 bg-ink text-paper px-8 h-12 leading-[3rem] font-mono text-sm uppercase tracking-widest hover:bg-link transition-colors">Back to Catalog</Link>
        </div>
      )}
      {(status === "failed" || status === "timeout") && (
        <div data-testid="payment-failed" className="border-2 border-danger p-10">
          <XCircle className="h-16 w-16 mx-auto text-danger" />
          <h1 className="font-heading font-black text-3xl tracking-tighter mt-6">{status === "timeout" ? "STILL PROCESSING" : "PAYMENT NOT COMPLETED"}</h1>
          <p className="font-mono text-sm text-subtle mt-3">{status === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation." : "The payment was cancelled or failed. Your cart is still saved."}</p>
          <Link to="/checkout" className="inline-block mt-8 bg-ink text-paper px-8 h-12 leading-[3rem] font-mono text-sm uppercase tracking-widest hover:bg-link transition-colors">Return to Checkout</Link>
        </div>
      )}
    </div>
  );
}
